import React, { useState } from "react";
import { Tag, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Button } from "../ui/button";
import { apiRequest } from "../../lib/utils";
import { toast } from "sonner";

const tagOptions = [
  { value: "easy", label: "Easy", color: "bg-green-100 text-green-800 border-green-200" },
  { value: "normal", label: "Normal", color: "bg-blue-100 text-blue-800 border-blue-200" },
  { value: "difficult", label: "Difficult", color: "bg-red-100 text-red-800 border-red-200" }
];

export const CustomerTagBadge = ({ 
  customerId, 
  currentTag = "normal", 
  onUpdate,
  editable = true,
  size = "default" // "default" or "small"
}) => {
  const [tag, setTag] = useState(currentTag);
  const [updating, setUpdating] = useState(false);

  const currentOption = tagOptions.find(t => t.value === tag) || tagOptions[1];

  const handleTagChange = async (newTag) => {
    if (newTag === tag) return;
    
    setUpdating(true);
    try {
      await apiRequest(`/customers/${customerId}`, {
        method: "PUT",
        body: JSON.stringify({ tag: newTag })
      });
      setTag(newTag);
      toast.success("Customer tag updated");
      onUpdate?.();
    } catch (error) {
      toast.error(error.message || "Failed to update tag");
    } finally {
      setUpdating(false);
    }
  };

  const sizeClasses = size === "small" 
    ? "px-1.5 py-0.5 text-xs gap-1"
    : "px-2 py-1 text-xs gap-1.5";

  if (!editable) {
    return (
      <span
        className={`inline-flex items-center rounded-full border font-medium ${sizeClasses} ${currentOption.color}`}
        data-testid={`customer-tag-${customerId}`}
      >
        <Tag className={size === "small" ? "w-3 h-3" : "w-3.5 h-3.5"} />
        {currentOption.label}
      </span>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`h-auto p-0 hover:bg-transparent ${updating ? "opacity-50" : ""}`}
          disabled={updating}
          data-testid={`customer-tag-trigger-${customerId}`}
        >
          <span
            className={`inline-flex items-center rounded-full border font-medium cursor-pointer hover:opacity-80 transition-opacity ${sizeClasses} ${currentOption.color}`}
          >
            <Tag className={size === "small" ? "w-3 h-3" : "w-3.5 h-3.5"} />
            {currentOption.label}
            <ChevronDown className={size === "small" ? "w-3 h-3" : "w-3.5 h-3.5"} />
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-32">
        {tagOptions.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onClick={() => handleTagChange(option.value)}
            className="cursor-pointer"
            data-testid={`tag-option-${option.value}`}
          >
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${option.color}`}>
              <Tag className="w-3 h-3" />
              {option.label}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default CustomerTagBadge;
